import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import SidebarTile from '../components/SidebarTile';
import MediaTile from '../components/MediaTile';
import { getStatusOptions } from '../utils/statusOptions';
import { itemMatchesFilter } from '../utils/filterEngine';

const MediaListPage = ({ apiBaseUrl }) => {
  const { mediaType } = useParams();
  const { userRoles } = useAuth();
  const canViewExplicit = userRoles?.includes('view-explicit');

  const [mediaItems, setMediaItems] = useState([]);
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterLogic, setFilterLogic] = useState('and');
  const [filters, setFilters] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [enableAnimations, setEnableAnimations] = useState(() => {
    const saved = localStorage.getItem('enableAnimations');
    if (saved !== null) return saved === 'true';
    return window.innerWidth >= 768; // On for desktop, off for mobile
  });
  const previousMediaTypeRef = useRef(null);

  const updateMediaItem = (updatedMedia) => {
    setMediaItems(prev => prev.map(item => item.id === updatedMedia.id ? updatedMedia : item));
  };

  useEffect(() => {
    // Check if mediaType actually changed
    const isInitialLoad = previousMediaTypeRef.current === null;
    const isMediaTypeSwitch = previousMediaTypeRef.current !== mediaType && previousMediaTypeRef.current !== null;

    if (isMediaTypeSwitch) {
      // Reset on media type switch
      console.log('Media type switched to:', mediaType, 'Resetting search and filters');
      setSearch('');
      setFilters({});
      window.history.replaceState(null, null, window.location.pathname);
    } else if (isInitialLoad) {
      // Parse hash on initial load
      console.log('Initial load for media type:', mediaType, 'Parsing hash');
      const hash = window.location.hash.substring(1);
      if (hash) {
        const params = new URLSearchParams(hash);
        const searchParam = params.get('search');
        if (searchParam) {
          console.log('Setting search from hash:', searchParam);
          setSearch(decodeURIComponent(searchParam));
        }
        const filterParams = {};
        params.forEach((value, key) => {
          if (key !== 'search') {
            filterParams[key] = value.split(',');
            console.log(`Setting filter from hash: ${key}=${value}`);
          }
        });
        if (Object.keys(filterParams).length > 0) {
          setFilters(filterParams);
        }
      }
    }

    // Fetch media
    const COMPOSITE_TYPES = {
      ShowsAndMovies: ['TV', 'Movie', 'Anime'],
      Music: ['Vinyl', 'CD'],
    };

    if (COMPOSITE_TYPES[mediaType]) {
      Promise.all(
        COMPOSITE_TYPES[mediaType].map(t =>
          fetch(`${apiBaseUrl}/media?type=${t}`, { credentials: 'include' }).then(r => r.json())
        )
      )
        .then(results => {
          const combined = results.flat();
          console.log('Fetched composite media:', combined);
          setMediaItems(combined);
        })
        .catch(err => console.error('Error fetching composite media:', err));
    } else {
      fetch(`${apiBaseUrl}/media?type=${mediaType}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          console.log('Fetched media:', data);
          setMediaItems(data);
        })
        .catch(err => console.error('Error fetching media:', err));
    }

    // Update previous mediaType
    previousMediaTypeRef.current = mediaType;
  }, [apiBaseUrl, mediaType]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    for (const [key, values] of Object.entries(filters)) {
      if (values.length > 0) params.set(key, values.join(','));
    }
    const newHash = params.toString() ? '#' + params.toString() : '';
    window.history.replaceState(null, null, newHash || window.location.pathname);
  }, [search, filters]);

  useEffect(() => {
    localStorage.setItem('enableAnimations', enableAnimations);
    console.log('Animations enabled:', enableAnimations);
  }, [enableAnimations]);

  const filterAndSortList = () => {
    let filtered = [...mediaItems];
    if (search) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(search.toLowerCase())
        // || (item.genre && item.genre.toLowerCase().includes(search.toLowerCase()))
      );
    }
    if (Object.keys(filters).length > 0) {
      filtered = filtered.filter(item => {
        const filterKeys = Object.keys(filters);
        return filterLogic === 'and'
          ? filterKeys.every(type => itemMatchesFilter(type, item, filters[type]))
          : filterKeys.some(type => itemMatchesFilter(type, item, filters[type]));
      });
    }
    filtered.sort((a, b) => {
      let aValue = a[sortOption] || 0;
      let bValue = b[sortOption] || 0;
      if (sortOption === 'title' || sortOption === 'status') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      const diff = parseFloat(bValue) - parseFloat(aValue);
      return sortDirection === 'asc' ? -diff : diff;
    });
    return filtered;
  };

  const handleSearch = (e) => setSearch(e.target.value);
  const handleSortChange = (e) => {
    console.log('Sorting by:', e.target.value);
    setSortOption(e.target.value);
  };
  const toggleSortDirection = () => {
    console.log('Toggling sort direction to:', sortDirection === 'desc' ? 'asc' : 'desc');
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };
  const handleFilterLogic = (e) => {
    console.log('Filter logic changed to:', e.target.checked ? 'or' : 'and');
    setFilterLogic(e.target.checked ? 'or' : 'and');
  };
  const handleFilterChange = (type, value, checked) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[type]) newFilters[type] = [];
      if (checked) newFilters[type].push(value);
      else newFilters[type] = newFilters[type].filter(v => v !== value);
      if (newFilters[type].length === 0) delete newFilters[type];
      return newFilters;
    });
  };

  const toggleAnimations = () => {
    setEnableAnimations(prev => {
      console.log('Toggling animations to:', !prev);
      return !prev;
    });
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleSwipe = (e) => {
    const touch = e.changedTouches[0];
    const startY = touch.clientY;
    const onTouchMove = (moveEvent) => {
      const currentY = moveEvent.touches[0].clientY;
      if (currentY - startY > 100) {
        closeSidebar();
        document.removeEventListener('touchmove', onTouchMove);
      }
    };
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', () => {
      document.removeEventListener('touchmove', onTouchMove);
    }, { once: true });
  };

  const statusOptions = getStatusOptions(mediaType);
  const visibleItems = filterAndSortList();

  return (
    <div className="media-container">
      <SidebarTile
        mediaType={mediaType}
        statusOptions={statusOptions}
        search={search}
        onSearch={handleSearch}
        sortOption={sortOption}
        onSortChange={handleSortChange}
        sortDirection={sortDirection}
        onToggleSortDirection={toggleSortDirection}
        filterLogic={filterLogic}
        onFilterLogicChange={handleFilterLogic}
        filters={filters}
        onFilterChange={handleFilterChange}
        visibleItems={visibleItems}
        allItems={mediaItems}
        isSidebarOpen={isSidebarOpen}
        enableAnimations={enableAnimations}
        onToggleAnimations={toggleAnimations}
        canViewExplicit={canViewExplicit}
      />
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
        id="sidebar-overlay"
        onClick={closeSidebar}
        onTouchStart={handleSwipe}
      ></div>
      <div id="media-list" className={enableAnimations ? '' : 'no-transition'}>
        <div className="sticky-search">
          <input
            type="text"
            id="search-bar-filter-mobile"
            className="input-field mobile-search"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
          />
          <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
            <img src="/static/icons/sliders-solid.svg" className="icon" style={{ height: '20px', width: '20px' }} alt="Toggle Sidebar" />
          </button>
        </div>
        <TransitionGroup component="div" className="media-tiles">
          {visibleItems.map(media => {
            const nodeRef = React.createRef();
            return (
              <CSSTransition
                key={media.id}
                timeout={enableAnimations ? 500 : 0}
                classNames="media-tile"
                nodeRef={nodeRef}
              >
                <div className="media-tile-wrapper" ref={nodeRef}>
                  <MediaTile
                    media={media}
                    statusOptions={getStatusOptions(media.type)}
                    apiBaseUrl={apiBaseUrl}
                    onUpdate={updateMediaItem}
                    mediaType={mediaType}
                  />
                </div>
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </div>
    </div>
  );
};

export default MediaListPage;